import backendAxios from '../utils/backendAxios';
import {
  GET_CHATS,
  GET_MESSAGES,
  ADD_MESSAGE,
  SET_CURRENT_CHAT,
  CHAT_ERROR,
  TOGGLE_CHAT,
  MARK_CHAT_READ,
  DELETE_CHAT,
  CHAT_BLOQUEO
} from './types';

// ... (other actions stay the same)

// Delete a chat logically
export const deleteChat = (chatId) => async dispatch => {
  try {
    await backendAxios.delete(`/api/chat/${chatId}`);
    dispatch({
      type: DELETE_CHAT,
      payload: chatId
    });
  } catch (err) {
    console.error('Error deleting chat:', err);
    dispatch({
      type: CHAT_ERROR,
      payload: { msg: "Error al eliminar conversación" }
    });
  }
};

// Bloquea (o desbloquea) a la otra persona de una conversación. Devuelve { ok } o { ok: false, error } para avisar a la persona.
export const bloquearChat = (chatId, bloquear) => async dispatch => {
  try {
    const res = await backendAxios.post(`/api/chat/${chatId}/${bloquear ? 'bloquear' : 'desbloquear'}`);
    dispatch({ type: CHAT_BLOQUEO, payload: { chatId, bloqueadoPorMi: Boolean(res.data.bloqueadoPorMi) } });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err.response?.data?.msg || 'No se pudo completar la acción. Intenta de nuevo.' };
  }
};

// Reporta la conversación al equipo (crea un ticket con los últimos mensajes). Devuelve { ok, numero, repetido } o { ok: false, error }.
export const reportarChat = (chatId, { motivo, detalle }) => async () => {
  try {
    const res = await backendAxios.post(`/api/chat/${chatId}/reportar`, { motivo, detalle });
    return { ok: true, numero: res.data.numero, repetido: Boolean(res.data.repetido) };
  } catch (err) {
    return { ok: false, error: err.response?.data?.msg || 'No se pudo enviar el reporte. Intenta de nuevo.' };
  }
};

// Cada cuánto se permite reenviar el mensaje automático de "Cotizar" (24h)
const COTIZAR_THROTTLE_MS = 24 * 60 * 60 * 1000;

// Decide si se debe enviar el mensaje automático: solo una vez cada 24h por
// (proveedor + servicio/página), para que al dar click varias veces a "Cotizar"
// no se repita el mismo mensaje en el chat. El usuario sí puede escribir manualmente.
const debeEnviarMensajeAuto = (throttleKey) => {
  if (!throttleKey) return true;
  try {
    const key = `cotizarAuto_${throttleKey}`;
    const ultimo = Number(localStorage.getItem(key)) || 0;
    if (Date.now() - ultimo < COTIZAR_THROTTLE_MS) return false;
    localStorage.setItem(key, String(Date.now()));
    return true;
  } catch (e) {
    // localStorage no disponible (modo privado, etc.): enviar de todos modos
    return true;
  }
};

// Get or create chat
export const startChat = (recipientId, relatedId, relatedType, initialMsg = null, throttleKey = null) => async dispatch => {
  dispatch(toggleChat(true)); // Abrir ventana inmediatamente para dar feedback
  dispatch({ type: CHAT_ERROR, payload: {} }); // quita un aviso anterior
  try {
    // Con mensaje inicial es el botón "Cotizar": el servidor avisa al proveedor por campanita y correo
    const res = await backendAxios.post('/api/chat', { recipientId, relatedId, relatedType, cotizar: Boolean(initialMsg) });
    dispatch(setCurrentChat(res.data));

    // Si hay mensaje inicial, enviarlo solo si no se envió ya en las últimas 24h
    if (initialMsg && debeEnviarMensajeAuto(throttleKey)) {
      dispatch(sendMessage(res.data._id, initialMsg));
    }
  } catch (err) {
    console.error('Error en startChat:', err.response?.data || err.message);
    if (err.message === 'Network Error') {
      console.warn('Posible problema de Mixed Content (HTTPS vs HTTP) o el servidor está apagado.');
    }
    // `origen: 'iniciar'` marca el aviso que ChatFloating muestra (con el motivo que dio el servidor: límite de conversaciones, etc.)
    dispatch({
      type: CHAT_ERROR,
      payload: { msg: err.response?.data?.msg || "No se pudo iniciar la conversación", origen: 'iniciar' }
    });
  }
};

// Get all chats
export const getChats = () => async dispatch => {
  try {
    const res = await backendAxios.get('/api/chat');
    dispatch({
      type: GET_CHATS,
      payload: res.data
    });
  } catch (err) {
    dispatch({
      type: CHAT_ERROR,
      payload: { msg: "Error al obtener chats" }
    });
  }
};

// Get messages for a chat
export const getMessages = (chatId) => async dispatch => {
  try {
    const res = await backendAxios.get(`/api/chat/${chatId}/messages`);
    dispatch({
      type: GET_MESSAGES,
      payload: res.data
    });
  } catch (err) {
    dispatch({
      type: CHAT_ERROR,
      payload: { msg: "Error al obtener mensajes" }
    });
  }
};

// Send a message
// `archivos`: imágenes/PDF a adjuntar (objetos File). Con archivos la petición es multipart; sin ellos
// sigue siendo el JSON de siempre. Devuelve { ok } o { ok: false, error } para que la pantalla pueda
// avisar y conservar lo escrito (quien no lo necesita puede ignorar el resultado).
export const sendMessage = (chatId, content, quoteId = null, type = 'text', archivos = []) => async (dispatch, getState) => {
  try {
    let res;
    if (archivos.length > 0) {
      const formData = new FormData();
      formData.append('content', content || '');
      formData.append('type', type);
      if (quoteId) formData.append('quote', quoteId);
      archivos.forEach((archivo) => formData.append('adjuntos', archivo));
      res = await backendAxios.post(`/api/chat/${chatId}/message`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } else {
      res = await backendAxios.post(`/api/chat/${chatId}/message`, { content, quote: quoteId, type });
    }
    const state = getState();
    const authUser = state.auth.user || state.user?.user;
    dispatch({
      type: ADD_MESSAGE,
      payload: { ...res.data, userId: authUser?._id }
    });
    return { ok: true };
  } catch (err) {
    dispatch({
      type: CHAT_ERROR,
      payload: { msg: "Error al enviar mensaje" }
    });
    return { ok: false, error: err.response?.data?.msg || 'No se pudo enviar el mensaje' };
  }
};

// Add message from socket
export const addSocketMessage = (message, chatId, userId) => dispatch => {
  dispatch({
    type: ADD_MESSAGE,
    payload: { ...message, chat: chatId, userId }
  });
};

// Mark chat as read
export const markAsRead = (chatId) => async dispatch => {
  try {
    await backendAxios.patch(`/api/chat/${chatId}/read`);
    dispatch({
      type: MARK_CHAT_READ,
      payload: chatId
    });
  } catch (err) {
    console.error('Error marking as read:', err);
  }
};

// Set current chat
export const setCurrentChat = (chat) => dispatch => {
  dispatch({
    type: SET_CURRENT_CHAT,
    payload: chat
  });
  if (chat) {
    dispatch(getMessages(chat._id));
    dispatch(markAsRead(chat._id));
  }
};

export const toggleChat = (open) => dispatch => {
  dispatch({
    type: TOGGLE_CHAT,
    payload: open
  });
};
